import React, { useState } from 'react';
import styled from 'styled-components';
import { Plus, Edit2, Trash2, X, FileText, Calendar, Upload, File, Loader2 } from 'lucide-react';
import { useRapports, TYPES_RAPPORT } from '../../hooks/useRapports';
import AdminLayout from '../components/AdminLayout';

const Container = styled.div`
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  color: var(--text-dark);
  font-weight: 700;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--primary-blue);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: var(--radius-md);
  font-weight: 600;
  transition: var(--transition);

  &:hover {
    background: #004d8f;
  }
`;

const Table = styled.table`
  width: 100%;
  background: white;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
`;

const Th = styled.th`
  text-align: left;
  padding: 1rem;
  background: var(--off-white);
  font-weight: 600;
  color: var(--text-dark);
`;

const Td = styled.td`
  padding: 1rem;
  border-bottom: 1px solid var(--border-light);
`;

const CategoryBadge = styled.span`
  display: inline-block;
  padding: 0.35rem 0.75rem;
  border-radius: var(--radius-full);
  font-size: 0.85rem;
  font-weight: 600;
  background: #DBEAFE;
  color: #2563EB;
`;

const ActionButton = styled.button`
  padding: 0.5rem;
  border-radius: var(--radius-md);
  transition: var(--transition);
  margin-right: 0.5rem;

  &:hover {
    background: var(--background-light);
  }

  &.delete:hover {
    background: #FEE2E2;
    color: #DC2626;
  }
`;

const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: var(--radius-lg);
  padding: 2rem;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 600;
  color: var(--text-dark);
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid var(--border-light);
  border-radius: var(--radius-md);
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: var(--primary-blue);
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid var(--border-light);
  border-radius: var(--radius-md);
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: var(--primary-blue);
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid var(--border-light);
  border-radius: var(--radius-md);
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: var(--primary-blue);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const SubmitButton = styled.button`
  flex: 1;
  padding: 0.75rem;
  background: var(--primary-blue);
  color: white;
  border-radius: var(--radius-md);
  font-weight: 600;

  &:hover {
    background: #004d8f;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 0.75rem;
  background: var(--background-light);
  color: var(--text-dark);
  border-radius: var(--radius-md);
  font-weight: 600;

  &:hover {
    background: var(--border-light);
  }
`;

const CloseButton = styled.button`
  background: none;
  color: var(--text-light);
  
  &:hover {
    color: var(--text-dark);
  }
`;

const ErrorMessage = styled.div`
  background: #FEE2E2;
  color: #DC2626;
  padding: 0.75rem;
  border-radius: var(--radius-md);
  font-size: 0.9rem;
`;

const UploadBox = styled.div`
  border: 2px dashed ${props => props.$hasFile ? 'var(--primary-blue)' : 'var(--border-light)'};
  border-radius: var(--radius-md);
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: var(--transition);
  background: ${props => props.$hasFile ? 'rgba(0, 38, 100, 0.05)' : 'var(--background-light)'};
  
  &:hover {
    border-color: var(--primary-blue);
  }
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: #E0F2FE;
  border-radius: var(--radius-md);
  margin-top: 0.5rem;
  color: #0369A1;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: var(--border-light);
  border-radius: 4px;
  overflow: hidden;
  margin-top: 0.5rem;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: var(--primary-blue);
  width: ${props => props.$progress}%;
  transition: width 0.3s ease;
`;

const formatFileSize = (bytes) => {
  if (!bytes) return '0 Octets';
  const k = 1024;
  const sizes = ['Octets', 'Ko', 'Mo', 'Go'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const AdminRapports = () => {
  const { rapports, loading, uploadProgress, createRapport, editRapport, removeRapport } = useRapports();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRapport, setEditingRapport] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    type: TYPES_RAPPORT[0].value,
    description: ''
  });
  const [pdfFile, setPdfFile] = useState(null);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.title.trim()) {
      setError('Veuillez entrer un titre');
      return;
    }

    if (!editingRapport && !pdfFile) {
      setError('Veuillez sélectionner un fichier PDF');
      return;
    }

    let result;
    if (editingRapport) {
      result = await editRapport(editingRapport.id, formData, pdfFile, !!pdfFile);
    } else {
      result = await createRapport(formData, pdfFile);
    }

    if (result.success) {
      closeModal();
    } else {
      setError(result.error);
    }
  };

  const handleDelete = async (id, pdfPath) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce rapport ')) {
      await removeRapport(id, pdfPath);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setError('Veuillez sélectionner un fichier PDF');
      return;
    }

    setPdfFile(file);
    setError('');
  };

  const openModal = (rapport = null) => {
    if (rapport) {
      setEditingRapport(rapport);
      setFormData({
        title: rapport.title,
        type: rapport.type || TYPES_RAPPORT[0].value,
        description: rapport.description || ''
      });
    } else {
      setEditingRapport(null);
      setFormData({
        title: '',
        type: TYPES_RAPPORT[0].value,
        description: ''
      });
    }
    setPdfFile(null);
    setError('');
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingRapport(null);
    setPdfFile(null);
    setError('');
  };

  return (
    <AdminLayout title="Gestion des Rapports">
      <Container>
        <Header>
          <Title>Rapports</Title>
          <AddButton onClick={() => openModal()}>
            <Plus size={20} />
            Ajouter un rapport
          </AddButton>
        </Header>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <Loader2 size={48} className="animate-spin" />
            <p>Chargement...</p>
          </div>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Titre</Th>
                <Th>Type</Th>
                <Th>Date</Th>
                <Th>Fichier</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {rapports.length === 0 ? (
                <tr>
                  <Td colSpan="5" style={{ textAlign: 'center' }}>Aucun rapport enregistré</Td>
                </tr>
              ) : (
                rapports.map((rapport) => (
                  <tr key={rapport.id}>
                    <Td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <FileText size={16} />
                        {rapport.title}
                      </div>
                    </Td>
                    <Td>
                      <CategoryBadge>{rapport.type}</CategoryBadge>
                    </Td>
                    <Td>
                      {rapport.date.toLocaleDateString('fr-FR', { 
                        day: 'numeric', 
                        month: 'short', 
                        year: 'numeric' 
                      })}
                    </Td>
                    <Td>
                      {rapport.pdfUrl ? (
                        <span style={{ color: '#059669', fontSize: '0.9rem' }}>✓ PDF</span>
                      ) : (
                        <span style={{ color: '#DC2626', fontSize: '0.9rem' }}>✗ Aucun</span>
                      )}
                    </Td>
                    <Td>
                      <ActionButton onClick={() => openModal(rapport)}>
                        <Edit2 size={18} />
                      </ActionButton>
                      <ActionButton className="delete" onClick={() => handleDelete(rapport.id, rapport.pdfPath)}>
                        <Trash2 size={18} />
                      </ActionButton>
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        )}

        {isModalOpen && (
          <Modal onClick={closeModal}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <h2>{editingRapport ? 'Modifier' : 'Ajouter'} un rapport</h2>
                <CloseButton onClick={closeModal}>
                  <X size={24} />
                </CloseButton>
              </ModalHeader>

              <Form onSubmit={handleSubmit}>
                {error && <ErrorMessage>{error}</ErrorMessage>}

                <FormGroup>
                  <Label>Titre</Label>
                  <Input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Titre du rapport"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Type</Label>
                  <Select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  >
                    {TYPES_RAPPORT.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>Description</Label>
                  <TextArea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Description du rapport (optionnel)"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Fichier PDF {editingRapport && '(laisser vide pour garder le fichier actuel)'}</Label>
                  <UploadBox 
                    $hasFile={!!pdfFile}
                    onClick={() => document.getElementById('pdf-input').click()}
                  >
                    <Input
                      id="pdf-input"
                      type="file"
                      accept="application/pdf"
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                    />
                    <Upload size={32} />
                    <p>Cliquez pour sélectionner un PDF</p>
                    <small>Maximum 50 Mo</small>
                  </UploadBox>
                  
                  {pdfFile && (
                    <FileInfo>
                      <File size={20} />
                      <div>
                        <strong>{pdfFile.name}</strong>
                        <p style={{ fontSize: '0.85rem', margin: 0 }}>{formatFileSize(pdfFile.size)}</p>
                      </div>
                    </FileInfo>
                  )}

                  {uploadProgress > 0 && uploadProgress < 100 && (
                    <>
                      <ProgressBar>
                        <ProgressFill $progress={uploadProgress} />
                      </ProgressBar>
                      <small>{uploadProgress}%</small>
                    </>
                  )}
                </FormGroup>

                <ButtonGroup>
                  <CancelButton type="button" onClick={closeModal}>
                    Annuler
                  </CancelButton>
                  <SubmitButton type="submit" disabled={uploadProgress > 0}>
                    {uploadProgress > 0 ? 'Upload en cours...' : (editingRapport ? 'Modifier' : 'Ajouter')}
                  </SubmitButton>
                </ButtonGroup>
              </Form>
            </ModalContent>
          </Modal>
        )}
      </Container>
    </AdminLayout>
  );
};

export default AdminRapports;
